import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Check, ChevronsUpDown } from "lucide-react"
import { useState, useEffect } from "react"

export function FilterTypeSearch<T>({
  filterTypeData,
  isLoading,
  onSearch,
  value,
  onChange,
  width = "100%",
}: {
  filterTypeData: T[]
  isLoading: boolean
  onSearch: (query: string) => void
  value: string
  onChange: (val: string) => void
  width?: string | number
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState("")

  useEffect(() => {
    if (!value) {
      setInput("")
    }
  }, [value])

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (input.length >= 3) onSearch(input)
      else onSearch("")
    }, 300)
    return () => clearTimeout(handler)
  }, [input, onSearch])

  // 🧠 Helper: detect name-like field automatically
  const getLabel = (item: any): string => {
    return (
      item?.Name ||
      item?.FormName ||
      item?.Label ||
      item?.Title ||
      "" // fallback
    )
  }

  const getId = (item: any, index: number) => item?.ID || item?.FunctionId || index

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          style={{ width }}
          className="w-[450px] justify-between"
        >
          {value || "Select Name"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent style={{ width }}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search type..."
            value={input}
            onValueChange={setInput}
          />
          <CommandList>
            {isLoading ? (
              <CommandGroup>
                <CommandItem disabled>Searching...</CommandItem>
              </CommandGroup>
            ) : (
              <>
                <CommandEmpty>No types found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onChange("")
                      setOpen(false)
                    }}
                  >
                    All types
                    {value === "" && <Check className="ml-auto h-4 w-4" />}
                  </CommandItem>

                  {filterTypeData.map((item, i) => {
                    const label = getLabel(item)
                    const key = getId(item, i)

                    return (
                      <CommandItem
                        key={key}
                        onSelect={() => {
                          onChange(label)
                          setOpen(false)
                        }}
                      >
                        {label}
                        {value === label && <Check className="ml-auto h-4 w-4" />}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
